import { ofetch, type FetchOptions } from 'ofetch';
import { env, API_TIMEOUT_MS } from '@noor/config';
import { ApiError } from './error';
import { normalizeErrorBody } from './error-body';
import { readAuthToken, handleUnauthorized, refreshAuthToken } from './auth-token';
import { readLocale } from './locale';
import { logApiRequest, logApiResponse, resolveRequestUrl } from './request-log';
import { unwrapData } from './unwrap-data';

/**
 * Typed HTTP client with request/response interceptors and error
 * normalization. All entity `api/` segments call through this — they never
 * touch `fetch`/`ofetch` directly.
 *
 * Every request times out after `API_TIMEOUT_MS` by default. Callers may pass
 * an `AbortSignal` via `options.signal` (part of `FetchOptions`) for manual
 * cancellation — note ofetch then skips the default timeout, so the caller
 * owns the request's lifetime.
 */
const client = ofetch.create({
  baseURL: env.VITE_API_BASE_URL,
  retry: 0,
  timeout: API_TIMEOUT_MS,
  onRequest({ request, options }) {
    const headers = new Headers(options.headers);

    // Auth interceptor: attach the bearer token if present.
    const token = readAuthToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    // Locale interceptor: tell the backend which language to resolve its
    // localized fields in. Sent on every request — including writes, whose
    // responses carry resolved fields too — so the UI never has to guess.
    //
    // A caller-supplied header wins: reading one resource in several languages
    // at once is how the merchant editor recovers translations, and that must
    // not be clobbered by the app's current language.
    if (!headers.has('x-locale')) headers.set('x-locale', readLocale());

    // Platform marker so the backend can tell web sessions apart from mobile
    // ones. A caller-supplied value wins, like `x-locale` above.
    if (!headers.has('X-Platform')) headers.set('X-Platform', 'web');

    options.headers = headers;

    // Dev-only wire log (URL + headers) — after the interceptors, so what is
    // logged is what is actually sent. Silent in prod and under tests.
    logApiRequest(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_API_BASE_URL),
      headers,
    );
  },
  onResponse({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_API_BASE_URL),
      response.status,
    );
  },
  onResponseError({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_API_BASE_URL),
      response.status,
    );
    // Normalize every transport error into an ApiError. The body may be flat
    // (`{message}`) or nested (`{error:{message}}`) — see `error-body.ts`.
    const body = normalizeErrorBody(response._data);
    // `||` (not `??`) on purpose: an empty body message / statusText (undici
    // responses default statusText to '') must not produce an empty message.
    throw new ApiError({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' must fall through
      message: body.message || response.statusText || 'Request failed',
      status: response.status,
      ...(body.code !== undefined ? { code: body.code } : {}),
      ...(body.details !== undefined ? { details: body.details } : {}),
    });
  },
});

/**
 * `/ws/` HTTP endpoints share the host configured for the WebSocket service.
 * The variable is normally `wss://…` for WebSocket connections, while fetch
 * requires its HTTP equivalent (`https://…`).
 */
function websocketHttpBaseURL(): string {
  const base = new URL(env.VITE_WEBSOCKET_API_URL);
  if (base.protocol === 'wss:') base.protocol = 'https:';
  else if (base.protocol === 'ws:') base.protocol = 'http:';
  return base.toString().replace(/\/$/, '');
}

function isWebsocketApiPath(url: string): boolean {
  const path = /^https?:\/\//i.test(url) ? new URL(url).pathname : url;
  return /^\/?ws\//.test(path);
}

function requestOptions<T extends 'json' | 'blob'>(
  url: string,
  options?: FetchOptions<T>,
): FetchOptions<T> | undefined {
  if (!isWebsocketApiPath(url)) return options;
  return { ...options, baseURL: websocketHttpBaseURL() };
}

/**
 * Call the refresh endpoint directly through `client` (not `http`, to avoid
 * recursing through this same retry wrapper). Its own 401 is not retried —
 * `refreshAuthToken` treats any failure as "no session".
 *
 * Refresh lives on the auth service (cpanel `refreshTokens`:
 * `POST {authHost}/api/v1/auth/refresh`), never the main gateway. The pair
 * arrives wrapped in a `{data, meta}` envelope
 * (`{data:{access_token,refresh_token}}`), so the body is `unwrapData`d before
 * the tokens are read. A malformed/partial payload throws rather than storing
 * `undefined`: `refreshAuthToken` catches it and reports "no session", which is
 * what sends the user to `/login` instead of retrying with `Bearer undefined`.
 */
async function callRefresh(refreshToken: string) {
  const raw = await client<unknown>('/api/v1/auth/refresh', {
    baseURL: env.VITE_AUTH_API_URL,
    method: 'POST',
    body: { refresh_token: refreshToken },
  });
  const payload = unwrapData(raw) as Partial<{
    access_token: string;
    refresh_token: string;
  }> | null;
  const accessToken = payload?.access_token;
  const newRefreshToken = payload?.refresh_token;
  if (typeof accessToken !== 'string' || typeof newRefreshToken !== 'string') {
    throw new ApiError({ message: 'Malformed refresh response', status: 500 });
  }
  return { accessToken, refreshToken: newRefreshToken };
}

/**
 * Run one request, and on a 401 attempt exactly one silent token refresh
 * (shared across concurrent 401s — see `refreshAuthToken`) before retrying it
 * once with the new token. If there is no refresh token or the refresh call
 * itself fails, fall back to the legacy behaviour: clear the session and
 * bounce to `/login`.
 */
async function withAuthRetry<T>(
  request: () => Promise<T>,
  skipAuthRetry = false,
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (skipAuthRetry || !(error instanceof ApiError) || !error.isUnauthorized)
      throw error;

    const newToken = await refreshAuthToken(callRefresh);
    if (newToken === null) {
      handleUnauthorized();
      throw error;
    }
    // A refreshed token can still be rejected (revoked session, a merchant
    // whose access was withdrawn). Without this catch the retry's 401 escapes
    // as a plain query error and NOTHING sends the user to `/login` — the
    // screen would sit on a data-error state for what is really a dead session.
    try {
      return await request();
    } catch (retryError) {
      if (retryError instanceof ApiError && retryError.isUnauthorized) {
        handleUnauthorized();
      }
      throw retryError;
    }
  }
}

/**
 * Thin typed wrapper. Returns the parsed JSON body, typed by the caller.
 * `options` accepts everything ofetch does, including `signal` (AbortSignal)
 * for caller-side cancellation — it is forwarded to `fetch` untouched.
 */
export const http = {
  get: <T>(url: string, options?: FetchOptions<'json'>) =>
    withAuthRetry(() =>
      client<T>(url, { ...requestOptions(url, options), method: 'GET' }),
    ),
  post: <T>(
    url: string,
    body?: unknown,
    options?: FetchOptions<'json'> & { skipAuthRetry?: boolean },
  ) => {
    const { skipAuthRetry = false, ...fetchOptions } = options ?? {};
    return withAuthRetry(
      () =>
        client<T>(url, {
          ...requestOptions(url, fetchOptions),
          method: 'POST',
          body: body as never,
        }),
      skipAuthRetry,
    );
  },
  patch: <T>(url: string, body?: unknown, options?: FetchOptions<'json'>) =>
    withAuthRetry(() =>
      client<T>(url, {
        ...requestOptions(url, options),
        method: 'PATCH',
        body: body as never,
      }),
    ),
  put: <T>(url: string, body?: unknown, options?: FetchOptions<'json'>) =>
    withAuthRetry(() =>
      client<T>(url, {
        ...requestOptions(url, options),
        method: 'PUT',
        body: body as never,
      }),
    ),
  delete: <T>(url: string, options?: FetchOptions<'json'>) =>
    withAuthRetry(() =>
      client<T>(url, { ...requestOptions(url, options), method: 'DELETE' }),
    ),
  /** GET a binary response (file export). Returns the raw Blob. */
  getBlob: (
    url: string,
    options?: Omit<FetchOptions<'blob'>, 'responseType' | 'method'>,
  ) =>
    withAuthRetry(() =>
      client<Blob, 'blob'>(url, {
        ...requestOptions(url, options),
        method: 'GET',
        responseType: 'blob',
      }),
    ),
};
